import { clearWishlist, getWishlist, onWishlistChange, removeFromWishlist } from '@theme/wishlist';

const CARD_SECTION_ID = 'wishlist-item';
const MAX_PARALLEL_REQUESTS = 4;

/**
 * Wishlist page: renders a product card for every saved handle.
 * Cards are rendered by Shopify (sections/wishlist-item.liquid via the Section Rendering API),
 * so they show live prices, stock and the same markup as the collection cards.
 */
class WishlistPage extends HTMLElement {
  /** @type {(() => void) | undefined} */
  #unsubscribe;

  /** @type {Map<string, HTMLElement>} */
  #items = new Map();

  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#unsubscribe = onWishlistChange(this.#onChange);
    this.querySelector('[data-wishlist-clear]')?.addEventListener('click', this.#onClear);
    this.#load(getWishlist());
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
    this.#abort?.abort();
    this.querySelector('[data-wishlist-clear]')?.removeEventListener('click', this.#onClear);
  }

  /** @returns {HTMLElement | null} */
  get list() {
    return this.querySelector('[data-wishlist-list]');
  }

  get root() {
    return window.Shopify?.routes?.root ?? '/';
  }

  /** @param {string[]} handles */
  async #load(handles) {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const missing = handles.filter((handle) => !this.#items.has(handle));
    this.#setState(handles.length === 0 ? 'empty' : missing.length > 0 && this.#items.size === 0 ? 'loading' : 'ready');

    for (let index = 0; index < missing.length; index += MAX_PARALLEL_REQUESTS) {
      const batch = missing.slice(index, index + MAX_PARALLEL_REQUESTS);
      const results = await Promise.all(batch.map((handle) => this.#fetchCard(handle, signal)));
      if (signal.aborted) return;

      results.forEach((card, i) => {
        const handle = batch[i];
        if (!handle) return;
        if (card) {
          this.#items.set(handle, card);
        } else if (card === null) {
          // Product no longer exists or is unpublished
          removeFromWishlist(handle);
        }
      });
      this.#renderOrder(getWishlist());
    }

    this.#renderOrder(getWishlist());
    // Every request failed (network): show the error state instead of an endless loader
    if (this.#items.size === 0 && getWishlist().length > 0) this.#setState('error');
  }

  /**
   * @param {string} handle
   * @param {AbortSignal} signal
   * @returns {Promise<HTMLElement | null | undefined>} The card, null when the product is gone, undefined on network error.
   */
  async #fetchCard(handle, signal) {
    const url = `${this.root}products/${encodeURIComponent(handle)}?section_id=${CARD_SECTION_ID}`;
    try {
      const response = await fetch(url, { signal });
      if (response.status === 404) return null;
      if (!response.ok) return undefined;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = html.querySelector('.wishlist-item');
      if (!(card instanceof HTMLElement)) return null;

      const item = document.createElement('li');
      item.className = 'wishlist-page__item';
      item.dataset.handle = handle;
      item.append(document.importNode(card, true));
      return item;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return undefined;
      console.warn('[wishlist] Could not load product', handle, error);
      return undefined;
    }
  }

  /** @param {string[]} handles */
  #renderOrder(handles) {
    const list = this.list;
    if (!list) return;

    // Drop cards that were removed from the wishlist
    for (const [handle, item] of this.#items) {
      if (!handles.includes(handle)) {
        item.remove();
        this.#items.delete(handle);
      }
    }

    // Append in wishlist order (newest last); append() moves nodes already in the list
    handles.forEach((handle) => {
      const item = this.#items.get(handle);
      if (item) list.append(item);
    });

    const count = this.#items.size;
    this.#updateCount(count);
    this.#setState(handles.length === 0 ? 'empty' : count > 0 ? 'ready' : 'loading');
  }

  /** @param {number} count */
  #updateCount(count) {
    const counter = this.querySelector('[data-wishlist-count]');
    if (!(counter instanceof HTMLElement)) return;
    const template = count === 1 ? counter.dataset.one : counter.dataset.other;
    counter.textContent = (template ?? '[count]').replace('[count]', String(count));
  }

  /** @param {'loading' | 'ready' | 'empty' | 'error'} state */
  #setState(state) {
    this.dataset.state = state;
  }

  /** @param {string[]} handles */
  #onChange = (handles) => {
    const hasNew = handles.some((handle) => !this.#items.has(handle));
    if (hasNew) {
      this.#load(handles);
    } else {
      this.#renderOrder(handles);
    }
  };

  #onClear = () => {
    const button = this.querySelector('[data-wishlist-clear]');
    const message = button instanceof HTMLElement ? button.dataset.confirm : '';
    if (message && !window.confirm(message)) return;
    clearWishlist();
  };
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
